import React, { useMemo, useRef } from 'react';

import { useAppTakeoverPdp } from 'features/app-takeover/hooks/use-app-takeover';
import { ProductCta } from 'features/menu/components/product-detail/product-cta';
import { ProductCustomizationDrinks } from 'features/menu/components/product-detail/product-customization/product-customization-drinks';
import { ProductCustomizationSides } from 'features/menu/components/product-detail/product-customization/product-customization-sides';
import { ProductCustomizationPanel } from 'features/menu/components/product-detail/product-customization/styled';
import { ComboSlotType } from 'features/menu/components/product-detail/product-customization/types';
import { getComboSlotType } from 'features/menu/components/product-detail/product-customization/utils';
import { ProductDetailHeader } from 'features/menu/components/product-detail/product-detail-flow/product-detail-header';
import { getEventAttributesForCustomization } from 'features/menu/components/product-detail/utils';
import { useProductWizardContext } from 'features/menu/components/product-wizard';
import { useProductWizardFlow } from 'features/menu/components/product-wizard/use-product-wizard-flow';
import { Platform, ScrollView } from 'react-native';

import { InnerLayout, LayoutContainer } from 'components/layout/styled';
import { FullWidthAutoHeightImage } from 'components/ui/full-width-auto-height-image';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { t } from 'state/intl/translations';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { isCombo } from 'state/unified-menu/utils';

import { ProductCustomizationItem } from './product-customization-item';

export const ProductCustomization = () => {
  const isMobile = useIsMobileBreakpoint();
  const scrollViewRef = useRef<ScrollView | null>(null);

  const { indexedMenu } = useUnifiedMenuContext();
  const { currentFlowStep, id } = useProductWizardFlow();
  const { backgroundColor: takeoverBg, backgroundImage: takeoverBgImage } = useAppTakeoverPdp(id);
  const { menuObject, selectedProduct } = useProductWizardContext();

  const comboSlotType: ComboSlotType = useMemo(
    () => getComboSlotType(indexedMenu, currentFlowStep.slotKey!),
    [indexedMenu, currentFlowStep]
  );

  const getHeaderTitle = () => {
    let title = t('haveItYourWay');
    const productName = selectedProduct.name?.locale ?? '';

    if (isCombo(menuObject) && productName) {
      title = productName;
    } else if (comboSlotType === ComboSlotType.SIDES) {
      title = t('chooseSide');
    } else if (comboSlotType === ComboSlotType.DRINKS) {
      title = t('chooseDrink');
    }

    return title;
  };

  const eventAttributes = useMemo(() => {
    return getEventAttributesForCustomization({
      currentStep: currentFlowStep.step,
      menuObjectType: menuObject.type,
      comboSlotType,
    });
  }, [comboSlotType, currentFlowStep.step, menuObject.type]);

  const productCustomization = useMemo(() => {
    switch (comboSlotType) {
      case ComboSlotType.PICKER_ASPECT:
        return <ProductCustomizationItem scrollViewRef={scrollViewRef} />;

      case ComboSlotType.SIDES:
        return <ProductCustomizationSides />;
      case ComboSlotType.DRINKS:
        return <ProductCustomizationDrinks />;
      default:
        return null;
    }
  }, [comboSlotType]);

  return (
    <ProductCustomizationPanel>
      <ProductDetailHeader title={getHeaderTitle()} eventAttributes={eventAttributes ?? {}} />
      <ScrollView
        ref={scrollViewRef}
        showsVerticalScrollIndicator={!isMobile}
        style={{ backgroundColor: takeoverBg ?? 'white' }}
        removeClippedSubviews={Platform.OS === 'android'}
      >
        {takeoverBgImage && (
          <FullWidthAutoHeightImage source={takeoverBgImage} style={{ position: 'absolute' }} />
        )}
        <LayoutContainer isFullContainer={isMobile}>
          <InnerLayout>{productCustomization}</InnerLayout>
        </LayoutContainer>
      </ScrollView>
      <ProductCta />
    </ProductCustomizationPanel>
  );
};
